import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Map } from 'react-map-gl/mapbox';
import { DeckGL } from '@deck.gl/react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapStore } from '../store/useMapStore';
import { PointSettings } from '../../../types/metadata';
import { ScatterplotLayer, IconLayer, PolygonLayer } from '@deck.gl/layers';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { H3HexagonLayer } from '@deck.gl/geo-layers';
import { DEFAULT_POINT_SETTINGS } from '../constants/pointSettings';
import { createSvgIcon } from "../utils/svgUtils";
import { getDataLoader } from '../../../workers/dataLoaderClient';
import { recordLayerLoad, recordViewportRefresh, recordDeckLayersBuild, recordDataOverload } from '../../../monitoring/rum';
import { useColorCodeStore } from '../store/useColorCodeStore';

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

if (!MAPBOX_ACCESS_TOKEN) {
  throw new Error('REACT_APP_MAPBOX_ACCESS_TOKEN environment variable is required. Run ./setup-env.sh to configure.');
}

interface MapBackgroundProps {
  style?: React.CSSProperties;
  className?: string;
}

export const MapBackground: React.FC<MapBackgroundProps> = ({ style, className }) => {
  const viewport = useMapStore(state => state.viewport);
  const layerIds = useMapStore(state => state.layerIds);
  const layersConfig = useMapStore(state => state.layersConfig);
  const layersData = useMapStore(state => state.layersData);
  const layerFilters = useMapStore(state => state.layerFilters);
  const setViewport = useMapStore(state => state.setViewport);
  const updateLayerData = useMapStore(state => state.updateLayerData);
  const h3DebugCells = useMapStore(state => state.h3DebugCells);
  const setH3DebugCells = useMapStore(state => state.setH3DebugCells);
  const heatmapRanges = useMapStore(state => state.heatmapRanges);
  const showDebugHexagons = useMapStore(state => state.showDebugHexagons);
  const openOverlay = useMapStore(state => state.openOverlay);
  const catchmentArea = useMapStore(state => state.catchmentArea);
  const debugQueryBounds = useMapStore(state => state.debugQueryBounds);
  const rawViewportBounds = useMapStore(state => state.rawViewportBounds);
  const setDebugQueryBounds = useMapStore(state => state.setDebugQueryBounds);
  const setRawViewportBounds = useMapStore(state => state.setRawViewportBounds);

  const [viewState, setViewState] = useState({
    longitude: viewport.longitude,
    latitude: viewport.latitude,
    zoom: viewport.zoom,
  });
  
  // Debug: Log when h3DebugCells changes
  useEffect(() => {
    console.log('[MapBackground] h3DebugCells changed', {
      count: h3DebugCells.length,
      cells: h3DebugCells.slice(0, 5),
    });
  }, [h3DebugCells]);
  
  const deckRef = useRef<any>(null);
  const [isDeckInitialized, setIsDeckInitialized] = useState(false);

  const viewportTimeoutRef = useRef<NodeJS.Timeout>();
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;
  const lastQueryViewportRef = useRef<{ lat: number; lon: number; zoom: number } | null>(null);
  const isRefreshingRef = useRef(false);
  
  // Handle DeckGL initialization
  const handleDeckLoad = useCallback(() => {
    console.log('[MapBackground] 🎯 DeckGL initialized');
    setIsDeckInitialized(true);
  }, []);
  
  // Calculate viewport bounds from the actual DeckGL viewport
  const calculateViewportBounds = useCallback(() => {
    // Try to get bounds from DeckGL viewport if available
    if (isDeckInitialized && deckRef.current?.deck) {
      try {
        const deck = deckRef.current.deck;
        const deckViewport = deck.getViewports()[0];
        
        if (deckViewport && deckViewport.width && deckViewport.height) {
          // Get the four corners of the viewport
          const topLeft = deckViewport.unproject([0, 0]);
          const topRight = deckViewport.unproject([deckViewport.width, 0]);
          const bottomLeft = deckViewport.unproject([0, deckViewport.height]);
          const bottomRight = deckViewport.unproject([deckViewport.width, deckViewport.height]);
          
          // Calculate bounds from corners
          const lngs = [topLeft[0], topRight[0], bottomLeft[0], bottomRight[0]];
          const lats = [topLeft[1], topRight[1], bottomLeft[1], bottomRight[1]];
          
          const north = Math.max(...lats);
          const south = Math.min(...lats);
          const east = Math.max(...lngs);
          const west = Math.min(...lngs);
          
          // Add padding to ensure edge cells are included
          // Padding is ~10% of the viewport span to catch cells at edges
          const latPadding = (north - south) * 0.1;
          const lonPadding = (east - west) * 0.1;
          
          const bounds = {
            north: Math.min(north + latPadding, 85),
            south: Math.max(south - latPadding, -85),
            east: east + lonPadding,
            west: west - lonPadding,
          };
          
          console.log('[MapBackground] Calculated bounds from DeckGL viewport', {
            viewportSize: { width: deckViewport.width, height: deckViewport.height },
            rawBounds: { north, south, east, west },
            paddedBounds: bounds,
            padding: { lat: latPadding.toFixed(4), lon: lonPadding.toFixed(4) },
            size: {
              latSpan: (bounds.north - bounds.south).toFixed(4),
              lonSpan: (bounds.east - bounds.west).toFixed(4),
            },
          });
          
          return bounds;
        }
      } catch (error) {
        console.warn('[MapBackground] Failed to get DeckGL viewport, using fallback', error);
      }
    }
    
    // Fallback: calculate from viewState and window size
    const worldSize = 256 * Math.pow(2, viewState.zoom);
    const lonDegreesPerPixel = 360 / worldSize;
    const latRadians = viewState.latitude * Math.PI / 180;
    const latDegreesPerPixel = (360 / worldSize) / Math.cos(latRadians);
    
    // Use actual window size with 10% padding
    const lonOffset = (window.innerWidth / 2) * lonDegreesPerPixel * 1.1;
    const latOffset = (window.innerHeight / 2) * latDegreesPerPixel * 1.1;
    
    const bounds = {
      north: Math.min(viewState.latitude + latOffset, 85),
      south: Math.max(viewState.latitude - latOffset, -85),
      east: viewState.longitude + lonOffset,
      west: viewState.longitude - lonOffset,
    };
    
    console.log('[MapBackground] Calculated bounds (fallback)', {
      windowSize: { width: window.innerWidth, height: window.innerHeight },
      bounds,
      size: {
        latSpan: (bounds.north - bounds.south).toFixed(4),
        lonSpan: (bounds.east - bounds.west).toFixed(4),
      },
    });
    
    return bounds;
  }, [viewState, isDeckInitialized]);
  
  // Track heatmap thresholds to trigger reload when they change
  const heatmapThresholdsKey = useMemo(() => {
    return layerIds.map(id => layersConfig[id]?.pointSettings?.heatmapThreshold ?? 500).join(',');
  }, [layerIds, layersConfig]);

  // --- Color Code Data: compute which extra fields each layer needs ---
  const colorCodeFieldsMap = useMemo(() => {
    const result: Record<string, string[]> = {};
    for (const id of layerIds) {
      const config = layersConfig[id];
      if (!config) continue;
      const fields: string[] = [];

      // If this layer has colorCodeConfig, it needs its own joinField
      if (config.colorCodeConfig?.joinField) {
        fields.push(config.colorCodeConfig.joinField);
      }

      // If another layer references this layer as a source, this layer needs the sourceJoinField
      for (const otherId of layerIds) {
        if (otherId === id) continue;
        const otherConfig = layersConfig[otherId];
        if (otherConfig?.colorCodeConfig?.sourceLayerId === id && otherConfig.colorCodeConfig.sourceJoinField) {
          fields.push(otherConfig.colorCodeConfig.sourceJoinField);
        }
      }

      if (fields.length > 0) {
        result[id] = Array.from(new Set(fields)); // Deduplicate
      }
    }
    return result;
  }, [layerIds, layersConfig]);

  // Track color code field requirements to trigger reload when they change
  const colorCodeFieldsKey = useMemo(() => {
    return JSON.stringify(colorCodeFieldsMap);
  }, [colorCodeFieldsMap]);

  // Track layer filters to trigger reload when they change
  const layerFiltersKey = useMemo(() => {
    return JSON.stringify(layerFilters);
  }, [layerFilters]);

  // Reset last query viewport when heatmap thresholds change to force a full reload
  // BUT skip when going from empty to having layers (initial layer add)
  const prevThresholdsKeyRef = useRef(heatmapThresholdsKey);
  useEffect(() => {
    if (prevThresholdsKeyRef.current !== heatmapThresholdsKey) {
      const wasEmpty = prevThresholdsKeyRef.current === '';
      prevThresholdsKeyRef.current = heatmapThresholdsKey;
      // Don't reset viewport when first layer is added (going from '' to a value)
      // The normal refresh will pick it up
      if (!wasEmpty) {
        lastQueryViewportRef.current = null;
      }
    }
  }, [heatmapThresholdsKey]);

  // Reload layers when color code fields change (need to fetch new field data)
  const prevColorCodeFieldsKeyRef = useRef(colorCodeFieldsKey);
  useEffect(() => {
    if (prevColorCodeFieldsKeyRef.current === colorCodeFieldsKey) return;
    const wasEmpty = prevColorCodeFieldsKeyRef.current === '{}';
    prevColorCodeFieldsKeyRef.current = colorCodeFieldsKey;
    if (wasEmpty) return; // Skip initial setup

    // Force reload all visible layers with the new colorCodeFields
    const reloadWithColorCodeFields = async () => {
      if (!isDeckInitialized) return;
      const bounds = calculateViewportBounds();
      if (!bounds) return;

      const dataLoader = getDataLoader();
      for (const layerId of layerIds) {
        const config = layersConfig[layerId];
        if (config && config.visible) {
          try {
            const result = await dataLoader.loadLayerData(config, {
              latitude: viewport.latitude,
              longitude: viewport.longitude,
              zoom: viewport.zoom,
              bounds,
            }, layerFilters[layerId], colorCodeFieldsMap[layerId]);
            updateLayerData(config.id, result.data);
          } catch (error) {
            console.error(`[MapBackground] Failed to reload layer for color code: ${config.name}`, error);
          }
        }
      }
    };

    reloadWithColorCodeFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorCodeFieldsKey]);

  // Reload layers when filters change (separate from viewport-based refresh)
  const prevFiltersKeyRef = useRef(layerFiltersKey);
  const filtersInitializedRef = useRef(false);
  useEffect(() => {
    if (!filtersInitializedRef.current) {
      filtersInitializedRef.current = true;
      return;
    }
    if (prevFiltersKeyRef.current === layerFiltersKey) return;
    prevFiltersKeyRef.current = layerFiltersKey;

    // Filters changed — reload all visible layers with new filters
    const reloadWithFilters = async () => {
      if (!isDeckInitialized) return;

      const bounds = calculateViewportBounds();
      if (!bounds) return;

      const dataLoader = getDataLoader();
      for (const layerId of layerIds) {
        const config = layersConfig[layerId];
        if (config && config.visible) {
          try {
            const result = await dataLoader.loadLayerData(config, {
              latitude: viewport.latitude,
              longitude: viewport.longitude,
              zoom: viewport.zoom,
              bounds,
            }, layerFilters[layerId], colorCodeFieldsMap[layerId]);
            updateLayerData(config.id, result.data);
          } catch (error) {
            console.error(`[MapBackground] Failed to reload layer with filters: ${config.name}`, error);
          }
        }
      }
    };

    reloadWithFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerFiltersKey]);

  // Refresh layer data when viewport changes significantly
  useEffect(() => {
    // Don't refresh until DeckGL is initialized
    if (!isDeckInitialized) {
      console.log('[MapBackground] ⏸️ Waiting for DeckGL initialization...');
      return;
    }
    
    // Prevent concurrent refreshes
    if (isRefreshingRef.current) {
      console.log('[MapBackground] ⏭️ Refresh already in progress, skipping');
      return;
    }
    
    const refreshLayerData = async () => {
      isRefreshingRef.current = true;
      const currentViewport = {
        lat: viewport.latitude,
        lon: viewport.longitude,
        zoom: viewport.zoom,
      };

      // Check if we need to refresh (significant movement or zoom change)
      const last = lastQueryViewportRef.current;
      if (last) {
        const latDiff = Math.abs(currentViewport.lat - last.lat);
        const lonDiff = Math.abs(currentViewport.lon - last.lon);
        const zoomDiff = Math.abs(currentViewport.zoom - last.zoom);
        
        // Check if resolution changed using actual H3 config
        const { getSettingsForZoom } = await import('../../../workers/h3Config');
        const currentSettings = getSettingsForZoom(currentViewport.zoom);
        const lastSettings = getSettingsForZoom(last.zoom);
        const resolutionChanged = currentSettings.aggRes !== lastSettings.aggRes || 
                                  currentSettings.filterRes !== lastSettings.filterRes;
        
        // Calculate zoom-dependent movement threshold
        // At low zoom (viewing large area), need larger movement to trigger refresh
        // At high zoom (viewing small area), smaller movement should trigger refresh
        // Formula: threshold increases as zoom decreases (exponential relationship)
        const baseThreshold = 0.01; // Base threshold at high zoom
        const zoomFactor = Math.pow(2, 12 - currentViewport.zoom); // Exponential scaling
        const movementThreshold = baseThreshold * zoomFactor;
        
        console.log('[MapBackground] Viewport change detected', {
          current: currentViewport,
          last,
          diff: { latDiff: latDiff.toFixed(4), lonDiff: lonDiff.toFixed(4), zoomDiff: zoomDiff.toFixed(2) },
          settings: { 
            current: currentSettings, 
            last: lastSettings, 
            changed: resolutionChanged 
          },
          threshold: movementThreshold.toFixed(4),
        });
        
        // Skip if movement is too small AND resolution hasn't changed
        if (!resolutionChanged && latDiff < movementThreshold && lonDiff < movementThreshold && zoomDiff < 0.5) {
          console.log('[MapBackground] ⏭️ Skipping refresh - movement too small and no resolution change', {
            latDiff: latDiff.toFixed(4),
            lonDiff: lonDiff.toFixed(4),
            threshold: movementThreshold.toFixed(4),
          });
          isRefreshingRef.current = false;
          return;
        }
        
        if (resolutionChanged) {
          console.log('[MapBackground] 🔄 Settings changed - forcing refresh', {
            aggRes: { old: lastSettings.aggRes, new: currentSettings.aggRes },
            filterRes: { old: lastSettings.filterRes, new: currentSettings.filterRes },
          });
        }
      } else {
        console.log('[MapBackground] Initial viewport query', currentViewport);
      }

      lastQueryViewportRef.current = currentViewport;
      
      // Calculate viewport bounds from actual DeckGL viewport
      const bounds = calculateViewportBounds();

      // Store bounds for debug visualization
      if (showDebugHexagons) {
        setDebugQueryBounds(bounds);
      }
      // Always update raw viewport bounds (used by DataTable "Viewing: Map Area" filter)
      if (isDeckInitialized && deckRef.current?.deck) {
        try {
          const deck = deckRef.current.deck;
          const vp = deck.getViewports()[0];
          if (vp && vp.width && vp.height) {
            const tl = vp.unproject([0, 0]);
            const tr = vp.unproject([vp.width, 0]);
            const bl = vp.unproject([0, vp.height]);
            const br = vp.unproject([vp.width, vp.height]);
            setRawViewportBounds({
              north: Math.max(tl[1], tr[1], bl[1], br[1]),
              south: Math.min(tl[1], tr[1], bl[1], br[1]),
              east: Math.max(tl[0], tr[0], bl[0], br[0]),
              west: Math.min(tl[0], tr[0], bl[0], br[0]),
            });
          }
        } catch (e) { /* ignore */ }
      }

      console.log('[MapBackground] 🔄 Refreshing layers', {
        visibleLayers: layerIds.filter(id => layersConfig[id]?.visible),
        totalLayers: layerIds.length,
        bounds,
      });

      // Refresh all visible layers with spatial query
      const dataLoader = getDataLoader();
      let successCount = 0;
      let errorCount = 0;
      const refreshStartTime = performance.now();
      
      for (const layerId of layerIds) {
        const config = layersConfig[layerId];
        if (config && config.visible) {
          try {
            console.log(`[MapBackground] Loading layer: ${config.name} (${layerId})`);
            const startTime = performance.now();
            
            const result = await dataLoader.loadLayerData(config, {
              latitude: viewport.latitude,
              longitude: viewport.longitude,
              zoom: viewport.zoom,
              bounds,
            }, layerFilters[layerId], colorCodeFieldsMap[layerId]);
            
            const duration = performance.now() - startTime;
            const pointCount = result.data[0]?.length || 0;
            console.log(`[MapBackground] ✅ Layer loaded: ${config.name}`, {
              points: pointCount,
              cached: result.cached,
              mode: result.mode,
              resolution: result.resolution,
              h3Cells: result.h3Cells?.length || 0,
              duration: `${duration.toFixed(2)}ms`,
            });

            // Record to CloudWatch RUM
            recordLayerLoad({
              layerId: config.id,
              layerName: config.name,
              pointCount,
              durationMs: Math.round(duration),
              cached: !!result.cached,
              mode: result.mode,
              resolution: result.resolution,
              h3Cells: result.h3Cells?.length || 0,
              viewportZoom: viewport.zoom,
            });
            
            // Update the layer data in the store (not add, since it already exists)
            updateLayerData(config.id, result.data);
            
            // Update H3 debug cells if available
            if (result.h3Cells && result.h3Cells.length > 0) {
              console.log('[MapBackground] 🔍 Received H3 cells from worker', { 
                count: result.h3Cells.length,
                cells: result.h3Cells.slice(0, 5),
              });
              setH3DebugCells(result.h3Cells);
              console.log('[MapBackground] 🔍 Set H3 debug cells in store');
            } else {
              console.warn('[MapBackground] ⚠️ No H3 cells in worker response');
            }
            
            successCount++;
          } catch (error) {
            console.error(`[MapBackground] ❌ Failed to refresh layer ${layerId}:`, error);
            errorCount++;
          }
        }
      }
      
      const refreshDuration = performance.now() - refreshStartTime;

      // Record viewport refresh cycle to RUM
      recordViewportRefresh({
        visibleLayers: successCount + errorCount,
        totalLayers: layerIds.length,
        durationMs: Math.round(refreshDuration),
        successCount,
        errorCount,
        viewportZoom: viewport.zoom,
      });

      // Detect data overload: total points across all visible layers
      const totalPoints = layerIds.reduce((sum, id) => {
        const data = useMapStore.getState().layersData[id];
        return sum + (data ? data.flat().length : 0);
      }, 0);

      const DATA_OVERLOAD_THRESHOLD = 50000;
      if (totalPoints > DATA_OVERLOAD_THRESHOLD) {
        // Find the largest layer for context
        let largestName = '';
        let largestCount = 0;
        layerIds.forEach(id => {
          const data = useMapStore.getState().layersData[id];
          const count = data ? data.flat().length : 0;
          if (count > largestCount) {
            largestCount = count;
            largestName = layersConfig[id]?.name || id;
          }
        });

        recordDataOverload({
          totalPoints,
          layerCount: layerIds.length,
          viewportZoom: viewport.zoom,
          largestLayerName: largestName,
          largestLayerPoints: largestCount,
        });
      }

      // Clear H3 grid area since we're not using it anymore
      
      console.log('[MapBackground] 🏁 Refresh complete', {
        success: successCount,
        errors: errorCount,
        total: successCount + errorCount,
      });

      isRefreshingRef.current = false;
    };

    refreshLayerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport.latitude, viewport.longitude, viewport.zoom, isDeckInitialized, heatmapThresholdsKey]);
  
  const handleMove = useCallback((evt: any) => {
    setViewState(evt.viewState);
    
    if (viewportTimeoutRef.current) {
      clearTimeout(viewportTimeoutRef.current);
    }
    
    // Use ref to avoid stale closure and prevent callback recreation on every frame
    const isZoomChange = Math.abs(evt.viewState.zoom - viewStateRef.current.zoom) > 0.01;
    const timeout = isZoomChange ? 100 : 300;
    
    viewportTimeoutRef.current = setTimeout(() => {
      setViewport({
        latitude: evt.viewState.latitude,
        longitude: evt.viewState.longitude,
        zoom: evt.viewState.zoom,
      });
    }, timeout);
  }, [setViewport]);

  const handleClick = useCallback((info: any) => {
    if (!info.object) return;
    
    // Get the layer ID from the clicked layer
    const layerId = info.layer?.id;
    if (!layerId) return;
    
    // Find the layer config (strip suffix for icon layers)
    const configId = layerId.replace('-icon-debug', '');
    const config = layersConfig[configId];
    if (!config || !config.visible || !config.viewSettings?.type) return;
    
    // Open overlay with the appropriate type, including the source layer ID
    console.log('[MapBackground] Opening overlay', {
      type: config.viewSettings.type,
      data: info.object,
      layerId: configId,
    });
    
    openOverlay(config.viewSettings.type, { ...info.object, _layerId: configId });
  }, [layersConfig, openOverlay]);

  const flattenedLayersData = useMemo(() => {
    const result: Record<string, any[]> = {};
    Object.keys(layersData).forEach((id: string) => {
      result[id] = layersData[id].flat();
    });
    return result;
  }, [layersData]);
  
  const resolvedSettings = useMemo(() => {
    const result: Record<string, PointSettings> = {};
    layerIds.forEach((id: string) => {
      const config = layersConfig[id]?.pointSettings;
      result[id] = {...DEFAULT_POINT_SETTINGS, ...config};
    });
    return result;
  }, [layerIds, layersConfig]);

  // --- Color Code Data: compute color assignments when visible data changes ---
  const colorCodeAssignments = useColorCodeStore(state => state.assignments);
  const computeColorAssignments = useColorCodeStore(state => state.computeAssignments);

  useEffect(() => {
    // For each layer with colorCodeConfig, compute assignments based on viewport-visible points
    for (const id of layerIds) {
      const config = layersConfig[id];
      if (!config?.colorCodeConfig) continue;
      const { sourceLayerId, joinField, sourceJoinField } = config.colorCodeConfig;

      // Config must be complete
      if (!sourceLayerId || !joinField || !sourceJoinField) continue;
      const sourceConfig = layersConfig[sourceLayerId];
      if (!sourceConfig?.visible) continue;

      // If the source layer is in heatmap mode (not showing individual points),
      // color coding is pointless — skip it
      const sourceData = flattenedLayersData[sourceLayerId];
      if (!sourceData || sourceData.length === 0 || sourceData[0]?.h3Index) {
        // Source is in heatmap/aggregation mode or not loaded — clear assignments
        useColorCodeStore.getState().clearAssignments(id);
        continue;
      }

      // Get this layer's points
      const thisData = flattenedLayersData[id];
      if (!thisData || thisData.length === 0) continue;

      // Get viewport bounds to filter to only in-view points
      const bounds = rawViewportBounds;
      const viewportCenter: [number, number] = [viewport.longitude, viewport.latitude];

      // Collect points with their join values and coords, filtering to viewport
      const visiblePoints: { joinValue: string; lng: number; lat: number }[] = [];
      const seenValues = new Set<string>();
      for (const point of thisData) {
        const value = point.fields?.[joinField] ?? point.id;
        if (value === undefined || value === null) continue;

        const coords = point.coords;
        if (!coords || coords.length < 2) continue;
        const [lng, lat] = coords;

        // Filter to viewport bounds (if available)
        if (bounds) {
          if (lat < bounds.south || lat > bounds.north || lng < bounds.west || lng > bounds.east) {
            continue; // Outside viewport
          }
        }

        const strValue = String(value);
        if (!seenValues.has(strValue)) {
          seenValues.add(strValue);
          visiblePoints.push({ joinValue: strValue, lng, lat });
        }
      }

      computeColorAssignments(id, visiblePoints, viewportCenter, config.colorCodeConfig);
    }
  }, [layerIds, layersConfig, flattenedLayersData, computeColorAssignments, rawViewportBounds, viewport.longitude, viewport.latitude]);

  // Update heatmap ranges when data changes (separate from render)
  useEffect(() => {
    layerIds.forEach((id) => {
      const flattenedData = flattenedLayersData[id];
      if (!flattenedData || flattenedData.length === 0) {
        useMapStore.getState().setHeatmapRange(id, 0, 0);
        return;
      }
      
      // Check if this is H3 aggregation data
      const isH3Aggregation = flattenedData[0]?.h3Index;
      if (isH3Aggregation) {
        // Filter out items without valid recordCount before calculating min/max
        const validCounts = flattenedData
          .map((item: any) => item.recordCount)
          .filter((count: number) => count !== undefined && count !== null && count > 0);
        
        if (validCounts.length > 0) {
          const maxCount = Math.max(...validCounts);
          const minCount = Math.min(...validCounts);

          useMapStore.getState().setHeatmapRange(id, minCount, maxCount);
        } else {
          // No valid counts found
          console.warn(`[MapBackground] No valid counts found for ${id}`);
          useMapStore.getState().setHeatmapRange(id, 0, 0);
        }
      } else {
        // Clear heatmap range when not in H3 aggregation mode
        useMapStore.getState().setHeatmapRange(id, 0, 0);
      }
    });
  }, [layerIds, flattenedLayersData]);


  const deckLayers = useMemo(() => {
    const buildStart = performance.now();
    const layers = [];
    
    console.log('[MapBackground] Building deck layers', {
      h3DebugCells: h3DebugCells.length,
      showDebugHexagons,
      regularLayers: layerIds.length,
      hasCatchmentArea: !!catchmentArea,
    });
    
    // Add catchment area layer (render first = lowest in stack, behind everything)
    if (catchmentArea) {
      if (catchmentArea.type === 'polygon' && catchmentArea.coordinates.length > 0) {
        layers.push(new PolygonLayer({
          id: 'catchment-area-polygon',
          data: [{ polygon: catchmentArea.coordinates }],
          pickable: false,
          stroked: true,
          filled: true,
          getPolygon: (d: any) => d.polygon,
          getFillColor: [65, 135, 245, 30],
          getLineColor: [65, 135, 245, 180],
          getLineWidth: 2,
          lineWidthMinPixels: 2,
        }));
      } else if (catchmentArea.type === 'polygons' && catchmentArea.polygons.length > 0) {
        layers.push(new PolygonLayer({
          id: 'catchment-area-polygons',
          data: catchmentArea.polygons.map(p => ({ polygon: p.coordinates })),
          pickable: false,
          stroked: true,
          filled: true,
          getPolygon: (d: any) => d.polygon,
          getFillColor: [65, 135, 245, 20],
          getLineColor: [65, 135, 245, 180],
          getLineWidth: 2,
          lineWidthMinPixels: 2,
        }));
      } else if (catchmentArea.type === 'circle' && catchmentArea.center) {
        const radiusMeters = catchmentArea.radiusMiles * 1609.34;
        layers.push(new ScatterplotLayer({
          id: 'catchment-area-circle',
          data: [{ position: catchmentArea.center }],
          pickable: false,
          stroked: true,
          filled: true,
          radiusUnits: 'meters' as const,
          getPosition: (d: any) => d.position,
          getRadius: radiusMeters,
          getFillColor: [65, 135, 245, 30],
          getLineColor: [65, 135, 245, 180],
          getLineWidth: 2,
          lineWidthMinPixels: 2,
        }));
      } else if (catchmentArea.type === 'circles' && catchmentArea.circles.length > 0) {
        layers.push(new ScatterplotLayer({
          id: 'catchment-area-circles',
          data: catchmentArea.circles.map(c => ({ position: catchmentArea.center, radiusMeters: c.radiusMiles * 1609.34 })),
          pickable: false,
          stroked: true,
          filled: true,
          radiusUnits: 'meters' as const,
          getPosition: (d: any) => d.position,
          getRadius: (d: any) => d.radiusMeters,
          getFillColor: [65, 135, 245, 20],
          getLineColor: [65, 135, 245, 180],
          getLineWidth: 2,
          lineWidthMinPixels: 2,
        }));
      }
    }
    
    // Add H3 debug overlay layer (render first, so it's behind everything)
    if (showDebugHexagons && h3DebugCells.length > 0) {
      console.log('[MapBackground] Rendering H3 debug overlay', { 
        cells: h3DebugCells.length,
        sampleCells: h3DebugCells.slice(0, 3),
      });
      
      // Convert H3 cells to hexagon data
      const h3DebugData = h3DebugCells.map(cell => ({
        h3Index: cell,
      }));
      
      console.log('[MapBackground] H3 debug data prepared', {
        dataCount: h3DebugData.length,
        sampleData: h3DebugData.slice(0, 3),
      });
      
      layers.push(new H3HexagonLayer({
        id: 'h3-debug-overlay',
        data: h3DebugData,
        pickable: false,
        wireframe: true,
        filled: true,
        extruded: false,
        elevationScale: 0,
        getHexagon: (d: any) => d.h3Index,
        getFillColor: () => [255, 0, 0, 30], // Red with low opacity
        getLineColor: () => [255, 0, 0, 150], // Red border with higher opacity
        getLineWidth: 3,
        lineWidthMinPixels: 2,
        getElevation: () => 0,
        opacity: 1,
      }));
      
      console.log('[MapBackground] H3 debug layer added to layers array');
    } else {
      console.log('[MapBackground] No H3 debug cells to render');
    }
    
    // Add debug query bounds rectangle (shows exact bounding box sent to API)
    if (showDebugHexagons && debugQueryBounds) {
      const { north, south, east, west } = debugQueryBounds;
      const boundsPolygon = [
        [west, north],
        [east, north],
        [east, south],
        [west, south],
        [west, north], // close the ring
      ];
      
      layers.push(new PolygonLayer({
        id: 'debug-query-bounds',
        data: [{ polygon: boundsPolygon }],
        pickable: false,
        stroked: true,
        filled: false,
        getPolygon: (d: any) => d.polygon,
        getLineColor: [255, 255, 0, 220], // Yellow = padded bounds (sent to API)
        getLineWidth: 3,
        lineWidthMinPixels: 2,
      }));
    }
    
    // Add raw viewport rectangle (exact screen edge, no padding)
    if (showDebugHexagons && rawViewportBounds) {
      // Inset slightly so the line is visible on screen (not clipped by edge)
      const latInset = (rawViewportBounds.north - rawViewportBounds.south) * 0.005;
      const lonInset = (rawViewportBounds.east - rawViewportBounds.west) * 0.005;
      const rawPolygon = [
        [rawViewportBounds.west + lonInset, rawViewportBounds.north - latInset],
        [rawViewportBounds.east - lonInset, rawViewportBounds.north - latInset],
        [rawViewportBounds.east - lonInset, rawViewportBounds.south + latInset],
        [rawViewportBounds.west + lonInset, rawViewportBounds.south + latInset],
        [rawViewportBounds.west + lonInset, rawViewportBounds.north - latInset],
      ];
      
      layers.push(new PolygonLayer({
        id: 'debug-raw-viewport',
        data: [{ polygon: rawPolygon }],
        pickable: false,
        stroked: true,
        filled: false,
        getPolygon: (d: any) => d.polygon,
        getLineColor: [0, 255, 0, 255], // Green = exact viewport edge (no padding)
        getLineWidth: 2,
        lineWidthMinPixels: 2,
      }));
    }
    
    // Add regular layers - split into heatmap layers (bottom) and point/icon layers (top)
    // so that icons remain clickable even when a heatmap overlays them
    const heatmapLayers: any[] = [];
    const pointLayers: any[] = [];
    
    layerIds.forEach((id) => {
      const config = layersConfig[id];
      const flattenedData = flattenedLayersData[id];

      if (!config || !flattenedData || !config.visible) return;

      const pointSettings = resolvedSettings[id];

      // Check if this is H3 aggregation data (has h3Index property)
      const isH3Aggregation = flattenedData.length > 0 && flattenedData[0]?.h3Index;
      
      console.log(`[MapBackground] Layer rendering for ${config.name}:`, {
        dataCount: flattenedData.length,
        isH3Aggregation,
        heatmapType: pointSettings.heatmapType,
        layerCount: config.count,
        samplePoint: flattenedData[0],
        first3Points: flattenedData.slice(0, 3),
      });
      
      if (isH3Aggregation) {
        console.log(`[MapBackground] Rendering H3 aggregation layer: ${config.name}`, {
          hexagons: flattenedData.length,
          type: pointSettings.heatmapType,
          sampleData: flattenedData[0],
          allData: flattenedData.slice(0, 3), // Show first 3 items
        });
        
        // Calculate max count for color scaling
        const maxCount = Math.max(...flattenedData.map((item: any) => item.recordCount || 1));
        const minCount = Math.min(...flattenedData.map((item: any) => item.recordCount || 1));
        console.log(`[MapBackground] H3 color scale: minCount=${minCount}, maxCount=${maxCount}`);
        
        // Render based on type: "hex" or "cloud"
        if (pointSettings.heatmapType === "cloud") {
          // Render as HeatmapLayer (cloud/smooth heatmap)
          heatmapLayers.push(new HeatmapLayer({
            id: `${id}-heatmap`,
            data: flattenedData,
            pickable: false,
            getPosition: (d: any) => d.coords,
            getWeight: (d: any) => d.recordCount || 1,
            radiusPixels: pointSettings.heatmapRadiusPixels ?? 30,
            intensity: pointSettings.heatmapIntensity ?? 0.75,
            threshold: pointSettings.heatmapCloudThreshold ?? 0.05,
            opacity: pointSettings.heatmapOpacity,
            colorRange: [
              pointSettings.heatmapPrimaryColor,
              pointSettings.heatmapSecondaryColor,
            ],
            updateTriggers: {
              getWeight: [flattenedData],
              colorRange: [
                pointSettings.heatmapPrimaryColor,
                pointSettings.heatmapSecondaryColor,
              ],
              opacity: [pointSettings.heatmapOpacity],
              radiusPixels: [pointSettings.heatmapRadiusPixels],
              intensity: [pointSettings.heatmapIntensity],
              threshold: [pointSettings.heatmapCloudThreshold],
            },
          }));
        } else {
          // Render as H3HexagonLayer (hex grid)
          heatmapLayers.push(new H3HexagonLayer({
            id: `${id}-h3`,
            data: flattenedData,
            pickable: false,
            wireframe: pointSettings.heatmapStroke > 0,
            filled: true,
            extruded: false,
            elevationScale: 0,
            getHexagon: (d: any) => d.h3Index,
            getFillColor: (d: any) => {
              const count = d.recordCount || 1;
              const range = heatmapRanges[id];
              const maxCount = range?.max || 1;
              const intensity = Math.min(count / maxCount, 1);

              // Lerp between primary and secondary colors
              const primary = pointSettings.heatmapPrimaryColor;
              const secondary = pointSettings.heatmapSecondaryColor;

              const r = Math.floor(
                primary[0] + (secondary[0] - primary[0]) * intensity,
              );
              const g = Math.floor(
                primary[1] + (secondary[1] - primary[1]) * intensity,
              );
              const b = Math.floor(
                primary[2] + (secondary[2] - primary[2]) * intensity,
              );

              return [r, g, b, Math.floor(pointSettings.heatmapOpacity * 255)];
            },
            getLineColor: [
              pointSettings.heatmapStrokeColor[0],
              pointSettings.heatmapStrokeColor[1],
              pointSettings.heatmapStrokeColor[2],
              255,
            ] as [number, number, number, number],
            getLineWidth: pointSettings.heatmapStroke,
            lineWidthMinPixels: pointSettings.heatmapStroke > 0 ? pointSettings.heatmapStroke : 0,
            getElevation: () => 0,
            opacity: pointSettings.opacity,
            updateTriggers: {
              getFillColor: [
                flattenedData,
                pointSettings.heatmapPrimaryColor,
                pointSettings.heatmapSecondaryColor,
                pointSettings.heatmapOpacity,
              ],
              getLineColor: [
                pointSettings.heatmapStrokeColor,
              ],
              getLineWidth: [pointSettings.heatmapStroke],
              wireframe: [pointSettings.heatmapStroke],
              opacity: [pointSettings.opacity],
            },
          }));
        }
        return;
      }

      // Render icon layer if configured
      if (pointSettings.markerType === 'icon' || pointSettings.markerType === 'raw') {
        if (pointSettings.svg) {
          console.log(`[MapBackground] Rendering IconLayer for ${config.name}`);
          
          // Color Code Data: determine if we need dynamic icon coloring
          let colorCodeActive = false;
          let iconJoinField: string | null = null;
          let iconAssignments: Record<string, { color: [number, number, number] }> | null = null;
          let iconUnmatchedOpacity = 0.2;

          // Case 1: This layer has colorCodeConfig
          if (config.colorCodeConfig?.joinField && config.colorCodeConfig?.sourceLayerId) {
            const sourceLayerConfig = layersConfig[config.colorCodeConfig.sourceLayerId];
            if (sourceLayerConfig?.visible) {
              const layerAssignments = colorCodeAssignments[id];
              if (layerAssignments && Object.keys(layerAssignments).length > 0) {
                colorCodeActive = true;
                iconJoinField = config.colorCodeConfig.joinField;
                iconAssignments = layerAssignments;
                iconUnmatchedOpacity = config.colorCodeConfig.unmatchedOpacity ?? 0.2;
              }
            }
          }
          // Case 2: Another layer uses this layer as a source
          if (!colorCodeActive) {
            for (const otherId of layerIds) {
              if (otherId === id) continue;
              const otherConfig = layersConfig[otherId];
              if (otherConfig?.colorCodeConfig?.sourceLayerId === id && otherConfig.visible) {
                const otherAssignments = colorCodeAssignments[otherId];
                if (otherAssignments && Object.keys(otherAssignments).length > 0 && otherConfig.colorCodeConfig.sourceJoinField) {
                  colorCodeActive = true;
                  iconJoinField = otherConfig.colorCodeConfig.sourceJoinField;
                  iconAssignments = otherAssignments;
                  iconUnmatchedOpacity = otherConfig.colorCodeConfig.unmatchedOpacity ?? 0.2;
                  break;
                }
              }
            }
          }

          if (colorCodeActive && iconJoinField && iconAssignments) {
            // Pre-generate SVG icons for each palette color + default
            const iconCache: Record<string, string> = {};
            const getIconForColor = (color: [number, number, number]) => {
              const key = color.join(',');
              if (!iconCache[key]) {
                const svg = createSvgIcon({
                  svg: pointSettings.svg!,
                  color: pointSettings.color!,
                  size: 100,
                  backgroundColor: color, // Color code overrides background, not primary
                });
                iconCache[key] = btoa(svg);
              }
              return iconCache[key];
            };
            // Default icon
            const defaultSvg = createSvgIcon({
              svg: pointSettings.svg!,
              color: pointSettings.color!,
              size: 100,
              backgroundColor: pointSettings.borderColor!,
            });
            const defaultBase64 = btoa(defaultSvg);

            const joinFieldCapture = iconJoinField;
            const assignmentsCapture = iconAssignments;

            pointLayers.push(new IconLayer({
              id: `${id}-icon-debug`,
              data: flattenedData,
              pickable: !!config.viewSettings?.type,
              opacity: pointSettings.opacity,
              getPosition: (d: any) => d.coords,
              getIcon: (d: any) => {
                const value = d.fields?.[joinFieldCapture] ?? d.id;
                if (value !== undefined && value !== null) {
                  const assignment = assignmentsCapture[String(value)];
                  if (assignment) {
                    const base64 = getIconForColor(assignment.color);
                    return { url: `data:image/svg+xml;base64,${base64}`, width: 100, height: 100, anchorY: 50 };
                  }
                }
                return { url: `data:image/svg+xml;base64,${defaultBase64}`, width: 100, height: 100, anchorY: 50 };
              },
              getColor: (d: any) => {
                const value = d.fields?.[joinFieldCapture] ?? d.id;
                if (value !== undefined && value !== null) {
                  const assignment = assignmentsCapture[String(value)];
                  if (assignment) return [255, 255, 255, 255];
                }
                return [255, 255, 255, Math.round(iconUnmatchedOpacity * 255)];
              },
              getSize: pointSettings.size! * 8,
              sizeUnits: "pixels",
              updateTriggers: {
                opacity: [pointSettings.opacity],
                getIcon: [pointSettings.color, pointSettings.borderColor, pointSettings.svg, colorCodeAssignments],
                getColor: [colorCodeAssignments, iconUnmatchedOpacity],
                getSize: [pointSettings.size],
                pickable: [config.viewSettings?.type],
              },
            }));
          } else {
            // Standard icon rendering (no color coding)
            const processedSvg = createSvgIcon({
              svg: pointSettings.svg,
              color: pointSettings.color!,
              size: 100,
              backgroundColor: pointSettings.borderColor!,
            });
            const svgBase64 = btoa(processedSvg);
            
            pointLayers.push(new IconLayer({
              id: `${id}-icon-debug`,
              data: flattenedData,
              pickable: !!config.viewSettings?.type,
              opacity: pointSettings.opacity,
              getPosition: (d: any) => d.coords,
              getIcon: () => ({
                url: `data:image/svg+xml;base64,${svgBase64}`,
                width: 100,
                height: 100,
                anchorY: 50,
              }),
              getSize: pointSettings.size! * 8,
              sizeUnits: "pixels",
              updateTriggers: {
                opacity: [pointSettings.opacity],
                getIcon: [pointSettings.color, pointSettings.borderColor, pointSettings.svg],
                getSize: [pointSettings.size],
                pickable: [config.viewSettings?.type],
              },
            }));
          }
          return;
        }
      }
      
      console.log(`[MapBackground] Rendering ScatterplotLayer for ${config.name}`, {
        points: flattenedData.length,
        samplePoint: flattenedData[0],
        sampleCoords: flattenedData[0]?.coords,
        first5Points: flattenedData.slice(0, 5).map((d: any) => d.coords),
        settings: {
          size: pointSettings.size,
          color: pointSettings.color,
          opacity: pointSettings.opacity,
        },
        viewSettings: config.viewSettings,
        pickable: !!config.viewSettings?.type,
      });
      
      pointLayers.push(new ScatterplotLayer({
        id: id,
        data: flattenedData,
        pickable: !!config.viewSettings?.type, // Enable picking if viewSettings configured
        opacity: pointSettings.opacity,
        stroked: pointSettings.borderColor !== null,
        filled: true,
        radiusUnits: 'pixels',
        radiusScale: 1,
        lineWidthUnits: 'pixels',
        lineWidthScale: 1,
        getPosition: (d: any) => {
          // Validate coordinates
          const coords = d.coords;
          if (!coords || coords.length !== 2 || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') {
            console.warn('[ScatterplotLayer] Invalid coords:', coords);
            return [0, 0];
          }
          return coords;
        },
        getRadius: pointSettings.size! * 3, // Triple the size for better visibility
        getFillColor: (() => {
          // Color Code Data: determine if this layer should use dynamic coloring
          // Case 1: This layer has colorCodeConfig — color by its own joinField value
          if (config.colorCodeConfig?.joinField && config.colorCodeConfig?.sourceLayerId) {
            const sourceLayerConfig = layersConfig[config.colorCodeConfig.sourceLayerId];
            if (sourceLayerConfig?.visible) {
              const joinField = config.colorCodeConfig.joinField;
              const layerAssignments = colorCodeAssignments[id];
              if (layerAssignments && Object.keys(layerAssignments).length > 0) {
                const unmatchedAlpha = Math.round((config.colorCodeConfig.unmatchedOpacity ?? 0.2) * 255);
                return (d: any) => {
                  const value = d.fields?.[joinField] ?? d.id;
                  if (value !== undefined && value !== null) {
                    const assignment = layerAssignments[String(value)];
                    if (assignment) return [...assignment.color, 255];
                  }
                  return [...(pointSettings.color || [255, 0, 0]), unmatchedAlpha];
                };
              }
            }
          }
          // Case 2: Another layer uses this layer as a source — color by sourceJoinField
          for (const otherId of layerIds) {
            if (otherId === id) continue;
            const otherConfig = layersConfig[otherId];
            if (otherConfig?.colorCodeConfig?.sourceLayerId === id && otherConfig.visible) {
              const sourceJoinField = otherConfig.colorCodeConfig.sourceJoinField;
              const otherAssignments = colorCodeAssignments[otherId];
              if (otherAssignments && Object.keys(otherAssignments).length > 0 && sourceJoinField) {
                const unmatchedAlpha = Math.round((otherConfig.colorCodeConfig.unmatchedOpacity ?? 0.2) * 255);
                return (d: any) => {
                  const value = d.fields?.[sourceJoinField] ?? d.id;
                  if (value !== undefined && value !== null) {
                    const assignment = otherAssignments[String(value)];
                    if (assignment) return [...assignment.color, 255];
                  }
                  return [...(pointSettings.color || [255, 0, 0]), unmatchedAlpha];
                };
              }
            }
          }
          // Default: static color
          return pointSettings.color;
        })(),
        getLineColor: pointSettings.borderColor ?? [0, 0, 0],
        getLineWidth: pointSettings.borderSize! * (pointSettings.size!/5) * 1.5,
        updateTriggers: {
          opacity: [pointSettings.opacity],
          getFillColor: [pointSettings.color, colorCodeAssignments],
          stroked: [pointSettings.borderColor],
          getLineColor: [pointSettings.borderColor],
          getLineWidth: [pointSettings.borderSize, pointSettings.size],
          getRadius: [pointSettings.size],
          pickable: [config.viewSettings?.type],
        }
      }));
    });
    
    // Layer order: heatmaps first (bottom), then point/icon layers on top
    // This ensures icons are always clickable even when a heatmap overlays them
    layers.push(...heatmapLayers.reverse());
    layers.push(...pointLayers.reverse());

    // Record deck layer build performance to RUM
    const buildDuration = performance.now() - buildStart;
    const totalPoints = Object.values(flattenedLayersData).reduce((sum, d) => sum + d.length, 0);
    if (buildDuration > 5) { // Only record if build took more than 5ms to reduce noise
      recordDeckLayersBuild({
        layerCount: layers.length,
        totalPoints,
        h3DebugCells: h3DebugCells.length,
        durationMs: Math.round(buildDuration),
        viewportZoom: viewState.zoom,
      });
    }
    
    return layers;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerIds, layersConfig, flattenedLayersData, resolvedSettings, h3DebugCells, showDebugHexagons, heatmapRanges, catchmentArea, debugQueryBounds, rawViewportBounds, colorCodeAssignments]);

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        ...style,
      }}
    >
      <DeckGL
        ref={deckRef}
        viewState={viewState}
        onViewStateChange={handleMove}
        onClick={handleClick}
        onLoad={handleDeckLoad}
        controller={true}
        layers={deckLayers}
        pickingRadius={0}
        useDevicePixels={true}
      >
        <Map
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          mapStyle="mapbox://styles/mapbox/standard"
        />
      </DeckGL>
    </div>
  );
};